import axios from "axios";
import { casaServiceBaseUrl } from "../utils/config";
import { redirectToLogin } from "../utils/browserUtils";
import { getMarvinToken, getRegionID } from "../utils/authUtils";
import { isMiP } from "../utils/helpers";
import { attachPayloadCrypto } from "./payloadCrypto";

// Function to get the authorization token
const axiosInstanceWeb = axios.create({
  baseURL: casaServiceBaseUrl,
  headers: {
    Accept: "*/*",
    "Content-Type": "application/json",
    "User-Agent": false,
  },
  withCredentials: false, // Default to false; will be set to true for MiP in the code below
});

// The JWT is kept in memory only. Persisting it in sessionStorage let a previous
// tenant's token survive a tenant switch in the same tab. Purge legacy entries.
sessionStorage.removeItem("casa-authorization-token");

// Payload encryption (FSP-5412). Registered before the response interceptor below on purpose:
// axios runs response interceptors in registration order, so this one opens a sealed body before
// the handler below reads it, and retries a 409 from an expired session before anything can
// surface it to the user. It is a no-op outside the NextGen custom-domain deployment.
attachPayloadCrypto(axiosInstanceWeb);

if(isMiP()) {
  axiosInstanceWeb.defaults.withCredentials = true;
} else if (getMarvinToken() && getRegionID()) {
    // Use the fetch adapter so same-origin requests don't carry cookies. XHR always sends them
  // same-origin regardless of withCredentials; fetch honours credentials: 'omit'. Requires axios >= 1.7.
  // Guarded on the credentials being present: without them this would drop cookies AND send no
  // token, leaving the request with no credentials at all. Falling through keeps the XHR adapter,
  // which still sends same-origin cookies.
  axiosInstanceWeb.defaults.adapter = "fetch";
  axiosInstanceWeb.defaults.withCredentials = "omit";
  axiosInstanceWeb.defaults.headers.common["x-lsq-marvin-token"] =
    getMarvinToken();
  axiosInstanceWeb.defaults.headers.common["x-lsq-regionId"] = getRegionID();
}

// Add a request interceptor to capture the start time
axiosInstanceWeb.interceptors.request.use((config) => {
  config.startTime = performance.now(); // Record the start time
  return config;
});

axiosInstanceWeb.interceptors.response.use(
  (response) => {
    const endTime = performance.now(); // Record the end time
    const duration = endTime - response.config.startTime;
    response.responseTime = duration;
    if (response.headers?.authorization) {
      axiosInstanceWeb.defaults.headers.common["authorization"] =
        response.headers.authorization;
    }
    return response;
  },
  (error) => {
    if (error?.response?.status === 401) {
      redirectToLogin();
    }
    return Promise.reject(error);
  }
);

export default axiosInstanceWeb;
